import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Dataset, FileAction, FileColumnKey, FileModel, FolderViewModel, ListSearch, MEDFileColumnKeys, OptionItem, SortOption } from '@ca/core/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FILE_EXPLORER_MODE } from 'src/app/shared/constants/constants';
import * as actions from '../../../../../app-store/machine-learning-store/machine-learning.actions';
import * as selectors from '../../../../../app-store/machine-learning-store/machine-learning.selectors';
import { MachineLearningState } from '../../../../../app-store/machine-learning-store/machine-learning.state';
import { FileExplorerHistoryService } from '../../../../services/file-explorer-history.service';

@UntilDestroy()
@Component({
    selector: 'ca-file-explorer-machine-learning-tab',
    templateUrl: './file-explorer-machine-learning-tab.component.html',
    styleUrls: ['./file-explorer-machine-learning-tab.component.scss'],
    providers: [FileExplorerHistoryService],
})
export class FileExplorerMachineLearningTabComponent implements OnInit {
    /**
     * Used to hide and show the search bar in My Trainings and My Segmentations
     * (because search and sort doesn't work inside results (because in this case
     * it's all available on client side, and ListShellComponent can only search on server side))
     */
    get isDatasetRootFolder(): boolean {
        return this.fileExplorerHistoryService.getHistory().length === 1;
    }

    /**
     * Checks if we're inside of a step in the result (where it shows the list of files)
     * We can do this by checking the amount of history items, because the results always have the same structure
     *
     * For example:
     *
     * My Trainings
     *   |
     *    -  <Result Name>
     *       |
     *        - Step 1 | OME-TIFF List Converter
     *           |
     *             - <list of files>
     */
    get isResultInnerStepFolder(): boolean {
        return this.fileExplorerHistoryService.getHistory().length === 3;
    }

    get currentFolder(): FolderViewModel | undefined {
        return this.fileExplorerHistoryService.lastFolderOfHistory();
    }

    @Input() actionsForFilesTable: FileAction[] = [];
    @Input() columnsForFilesTable: FileColumnKey[] | MEDFileColumnKeys[] = [];
    @Input() shouldReloadMachineLearningTab: Observable<void>;
    @Input() foldersToHideIds: (string | null)[] = [];
    @Input() isFilesStatusShown?: boolean;

    @Output() onSelectFile = new EventEmitter<FileModel>();
    @Output() onMultipleFilesSelected = new EventEmitter<FileModel[]>();

    // Datasets
    allDatasets: Dataset[] = [];
    activeDataset: Dataset | undefined;

    foldersToShow: FolderViewModel[] = [];
    filesToShow: FileModel[] = [];
    totalRowCount: number = 0;
    isLoading = false;

    sortOptions: OptionItem<SortOption>[] = [
        { label: 'Sort by date desc', value: '-finishedAt' },
        { label: 'Sort by date asc', value: 'finishedAt' },
        { label: 'Sort by A-Z', value: 'name' },
        { label: 'Sort by Z-A', value: '-name' },
    ];

    readonly defaultPageSize = 10;
    readonly defaultPageNumber = 1;
    readonly defaultSearchParams: ListSearch = {
        categoryIds: '',
        pageNumber: this.defaultPageNumber,
        pageSize: this.defaultPageSize,
        searchQuery: '',
        sortBy: <SortOption>'-finishedAt',
    };

    currentDatasetSearchParams: ListSearch = this.defaultSearchParams;
    FILE_EXPLORER_MODE = FILE_EXPLORER_MODE;

    constructor(
        private fileExplorerHistoryService: FileExplorerHistoryService,
        private store$: Store<MachineLearningState>,
    ) {}

    ngOnInit(): void {
        this.openRootFolder();
        this.subscribeToDatasetsFromStore();
        this.subscribeToReloadEvent();
    }

    selectMultipleFiles(files: FileModel[]): void {
        if (files.length > 0) {
            this.onMultipleFilesSelected.emit(files);
        }
    }

    onDatasetListSearch(searchParams: ListSearch) {
        this.currentDatasetSearchParams = searchParams;
        this.loadDatasets();
    }

    openFolder(folder: FolderViewModel, openFolderFromBreadcrumbClick = false): void {
        // In case we were inside a dataset, clear it.
        // We need to do that to not show DatasetExplorer anymore (when we trigger this action from within a dataset)
        this.activeDataset = undefined;

        if (this.isDatasetRootFolder) {
            this.openMachineLearningFolderRoot(folder);
        } else {
            this.openResultFolder(folder, openFolderFromBreadcrumbClick);
        }
    }

    private openResultFolder(folder: FolderViewModel, openFolderFromBreadcrumbClick = false): void {
        this.emptyCurrentlyShownFilesAndFolders();

        if (!openFolderFromBreadcrumbClick) {
            // When we open a new folder, we need to put it into history
            this.fileExplorerHistoryService.navigateTo(folder);
        } else {
            // When we click on the breadcrumb, we cut the rest of the breadcrumb
            this.fileExplorerHistoryService.navigateTo(folder);
        }
    }

    selectFile(file: FileModel) {
        this.onSelectFile.emit(file);
    }

    openRootFolder(): void {
        // In case we were inside a dataset, clear it.
        this.activeDataset = undefined;
        this.emptyCurrentlyShownFilesAndFolders();
        this.prepareAndShowDatasets();
        this.fileExplorerHistoryService.clearHistory();
    }

    openDatasetFolder(folder: FolderViewModel): void {
        this.fileExplorerHistoryService.navigateTo(folder);

        const dataset: Dataset | undefined = this.allDatasets.find((d: Dataset) => d.datasetId === folder.id);
        if (dataset != null) {
            this.activeDataset = dataset;
        }
    }

    // Opens the 'Root' of My Datasets
    private openMachineLearningFolderRoot(folder: FolderViewModel) {
        this.fileExplorerHistoryService.clearHistory();
        this.emptyCurrentlyShownFilesAndFolders();
        this.fileExplorerHistoryService.navigateTo(folder);
        this.prepareAndShowDatasets();
    }

    private subscribeToReloadEvent(): void {
        this.shouldReloadMachineLearningTab
            .pipe(
                tap(() => {
                    this.openRootFolder();
                }),
            )
            .subscribe();
    }

    private prepareAndShowDatasets(): void {
        if (this.foldersToHideIds == null) {
            this.foldersToShow = this.mapDatasetsToFolders(this.allDatasets);
        } else {
            this.foldersToShow = this.mapDatasetsToFolders(this.allDatasets).filter(f => !this.foldersToHideIds.includes(f.id));
        }
    }

    private mapDatasetsToFolders(allDatasets: Dataset[]): FolderViewModel[] {
        const folders: FolderViewModel[] = allDatasets.map((dataset: Dataset) => {
            const folder: FolderViewModel = {
                id: dataset.datasetId,
                name: dataset.name,
                files: [],
                subfolders: [],
            };
            return folder;
        });
        return folders;
    }

    private subscribeToDatasetsFromStore() {
        this.store$
            .select(selectors.selectDatasetsTotalCount)
            .pipe(
                tap((totalCount: number) => {
                    this.totalRowCount = totalCount;
                }),
                untilDestroyed(this),
            )
            .subscribe();
        this.store$
            .select(selectors.selectDatasets)
            .pipe(
                tap((datasets: Dataset[]) => {
                    this.allDatasets = datasets || [];
                    this.prepareAndShowDatasets();
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    private loadDatasets() {
        this.store$.dispatch(actions.loadDatasets({ payload: { searchParams: this.currentDatasetSearchParams } }));
    }

    private emptyCurrentlyShownFilesAndFolders(): void {
        this.filesToShow = [];
        this.foldersToShow = [];
    }
}
